import { FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFacility, deleteFacility, updateFacility } from '../../lib/identityRegistry';
import { describeFacilityError } from './facilityHelpers';
import { FacilityEditDrawer } from './FacilityEditDrawer';
import { AreaCreateDialog } from './AreaCreateDialog';
import { AreaListDialog } from './AreaListDialog';
import { ConfirmDialog } from '../ConfirmDialog';
import type { AreaRecord, FacilityRecord } from '../../types/registry';

interface FacilityManagerProps {
  facilities: FacilityRecord[];
}

export function FacilityManager({ facilities }: FacilityManagerProps): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [editing, setEditing] = useState<FacilityRecord | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FacilityRecord | null>(null);
  const [viewingAreasFor, setViewingAreasFor] = useState<FacilityRecord | null>(null);
  const [addingAreaFor, setAddingAreaFor] = useState<FacilityRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['facilities'] });

  function resetCreateForm() {
    setName('');
    setAddress('');
    setCity('');
    setCountry('');
    setCreateError(null);
  }

  function closeCreate() {
    if (createMutation.isPending) return;
    setCreateOpen(false);
    resetCreateForm();
  }

  const createMutation = useMutation({
    mutationFn: () => createFacility({ name: name.trim(), address, city, country }),
    onSuccess: async () => {
      await invalidate();
      setCreateOpen(false);
      resetCreateForm();
      setError(null);
    },
    onError: (mutationError) => setCreateError(describeFacilityError(mutationError)),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFacility,
    onSuccess: async () => {
      await invalidate();
      setPendingDelete(null);
    },
    onError: (mutationError) => {
      setError(describeFacilityError(mutationError));
      setPendingDelete(null);
    },
  });

  const addAreaMutation = useMutation({
    mutationFn: ({ facility, area }: { facility: FacilityRecord; area: AreaRecord }) =>
      updateFacility(facility.facility_id, {
        area_ids: [...(facility.area_ids ?? []), area].map((entry) => ({
          area_id: entry.area_id,
          name: entry.name.trim(),
          floor: entry.floor ?? 0,
          description: entry.description ?? '',
        })),
      }),
    onSuccess: async () => {
      await invalidate();
      setAddingAreaFor(null);
      setError(null);
    },
    onError: (mutationError) => setError(describeFacilityError(mutationError)),
  });

  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreateError(null);
    createMutation.mutate();
  }

  return (
    <>
      <div className="panel-toolbar">
        <h2>Facilities</h2>
        <button
          className="primary-button"
          type="button"
          onClick={() => {
            setError(null);
            setCreateOpen(true);
          }}
        >
          Create facility
        </button>
      </div>

      {error ? <div className="form-error">{error}</div> : null}
      {!facilities.length ? <div className="empty-state">No facilities yet.</div> : null}
      {facilities.length ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Location</th>
              <th>Areas</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {facilities.map((facility) => (
              <tr key={facility.facility_id}>
                <td>{facility.name}</td>
                <td>{[facility.city, facility.country].filter(Boolean).join(', ') || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setViewingAreasFor(facility)}
                  >
                    {facility.area_ids?.length ?? 0}
                  </button>
                </td>
                <td>
                  <div className="table-actions">
                    <button type="button" onClick={() => setEditing(facility)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={addAreaMutation.isPending}
                      onClick={() => {
                        setError(null);
                        setAddingAreaFor(facility);
                      }}
                    >
                      Add area
                    </button>
                    <button
                      type="button"
                      disabled={deleteMutation.isPending}
                      onClick={() => {
                        setError(null);
                        setPendingDelete(facility);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {createOpen ? (
        <div className="modal-backdrop" onClick={closeCreate}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <h2>Create facility</h2>
            <p className="modal-card__copy">
              Add areas after creating the facility, from its Edit drawer.
            </p>
            <form className="invite-form" onSubmit={handleCreate}>
              <label className="form-field">
                <span>Name</span>
                <input value={name} onChange={(event) => setName(event.target.value)} required />
              </label>
              <label className="form-field">
                <span>Address</span>
                <input value={address} onChange={(event) => setAddress(event.target.value)} />
              </label>
              <label className="form-field">
                <span>City</span>
                <input value={city} onChange={(event) => setCity(event.target.value)} />
              </label>
              <label className="form-field">
                <span>Country</span>
                <input value={country} onChange={(event) => setCountry(event.target.value)} />
              </label>
              {createError ? <div className="form-error">{createError}</div> : null}
              <div className="modal-card__actions">
                <button
                  className="ghost-button"
                  type="button"
                  disabled={createMutation.isPending}
                  onClick={closeCreate}
                >
                  Cancel
                </button>
                <button className="primary-button" disabled={createMutation.isPending} type="submit">
                  {createMutation.isPending ? 'Creating...' : 'Create facility'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {editing ? (
        <FacilityEditDrawer facility={editing} onClose={() => setEditing(null)} />
      ) : null}

      {viewingAreasFor ? (
        <AreaListDialog facility={viewingAreasFor} onClose={() => setViewingAreasFor(null)} />
      ) : null}

      {pendingDelete ? (
        <ConfirmDialog
          title="Delete facility"
          message={`Delete facility "${pendingDelete.name}"? This cannot be undone.`}
          busy={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(pendingDelete.facility_id)}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}

      {addingAreaFor ? (
        <AreaCreateDialog
          saving={addAreaMutation.isPending}
          error={error}
          onCreate={(area) => addAreaMutation.mutate({ facility: addingAreaFor, area })}
          onCancel={() => {
            setAddingAreaFor(null);
            setError(null);
          }}
        />
      ) : null}
    </>
  );
}
